import { Box } from "@mui/material";
import { LineChart, LineChartProps } from "@mui/x-charts/LineChart";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { WorkerQueryTransferModes } from "../../libs/workers/consts";
import { WorkerQueryOptions } from "../../libs/workers/types";
import { webWorkerQuery } from "../../libs/workers/webWorker";

import {
  AggregationFn,
  AggregationPeriod,
  ChartClickEvent,
  ChartDataSet,
  ChartVendorProps,
  MetricConfig,
  TrendsOverChartEntry,
} from "./TrendsOverTimeChart.types";
import TrendsOverTimeWorker from "./TrendsOverTimeChart.worker?worker";

// ============================================
// Data fetching helpers
// ============================================

async function getChartData(
  entries: TrendsOverChartEntry[] | SharedArrayBuffer | ArrayBuffer,
  metrics: MetricConfig[],
  aggregationPeriod: AggregationPeriod,
  aggregationKey: string | undefined,
  aggregationFn: AggregationFn,
  opts?: WorkerQueryOptions
) {
  const worker = new TrendsOverTimeWorker();

  return webWorkerQuery(worker, {
    type: "PROCESS_CHART",
    data: {
      entries,
      metrics,
      aggregationPeriod,
      aggregationKey,
      aggregationFn,
    },
    opts: {
      traceKey: `TrendsOverTimeWorker-${Date.now()}`,
      ...opts,
      response: {
        transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        ...(opts?.response || {}),
      },
    },
  }).finally(() => {
    worker.terminate();
  });
}

function useChartData(
  key: string,
  timelineData: TrendsOverChartEntry[] | SharedArrayBuffer | ArrayBuffer,
  metrics: MetricConfig[],
  aggregationPeriod: AggregationPeriod,
  aggregationMode: "compare" | "stack",
  aggregationKey: string | undefined,
  aggregationFn: AggregationFn,
  pending = false,
  opts?: WorkerQueryOptions
) {
  return useQuery({
    queryKey: [
      "TrendsOverTimeChartData",
      key,
      metrics.map((m) => (m.enabled ? m.key : null)).join(","),
      aggregationPeriod,
      aggregationMode,
      aggregationKey,
      JSON.stringify(opts),
    ],
    queryFn: () =>
      getChartData(
        timelineData,
        metrics,
        aggregationPeriod,
        aggregationKey,
        aggregationFn,
        opts
      ),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !pending,
  });
}

// ============================================
// Utility functions
// ============================================

export function addValueFormatter(
  series?: ChartDataSet[],
  configs?: NonNullable<LineChartProps["yAxis"]>[number]
) {
  if (!configs || !series?.length) {
    return series;
  }

  return series.map((s) => ({
    ...s,
    valueFormatter: configs.valueFormatter,
  }));
}

// ============================================
// Event normalizers
// ============================================

const normalizeAreaClick = (
  xAxisData: string[],
  onDataPointClick?: (event: ChartClickEvent) => void
) => {
  if (!onDataPointClick) return undefined;

  return (event: any, data: any) => {
    const dataIndex = data?.dataIndex ?? 0;
    const normalizedEvent: ChartClickEvent = {
      dataIndex,
      periodLabel: xAxisData[dataIndex] ?? "",
      metricKey: data?.seriesId,
      value: data?.value,
      rawEvent: { event, data },
    };
    onDataPointClick(normalizedEvent);
  };
};

const normalizeAxisClick = (
  xAxisData: string[],
  onPeriodClick?: (event: ChartClickEvent) => void
) => {
  if (!onPeriodClick) return undefined;

  return (event: any, data: any) => {
    const dataIndex = data?.dataIndex ?? 0;
    const normalizedEvent: ChartClickEvent = {
      dataIndex,
      periodLabel: xAxisData[dataIndex] ?? "",
      rawEvent: { event, data },
    };
    onPeriodClick(normalizedEvent);
  };
};

// ============================================
// Component
// ============================================


export function MuiTrendsChart({
  cacheKey,
  entries,
  metrics,
  aggregationKey,
  aggregationPeriod,
  aggregationMode,
  defaultAggregationFn,
  yAxisConfig,
  width = 800,
  height = 400,
  grid,
  loading = false,
  opts,
  onDataPointClick,
  onPeriodClick,
}: Omit<ChartVendorProps, "comparator"> & {
  aggregationKey?: string;
  yAxisConfig?: NonNullable<LineChartProps["yAxis"]>[number];
}) {
  // Fetch chart data
  const chartData = useChartData(
    cacheKey,
    entries ?? [],
    metrics,
    aggregationPeriod,
    aggregationMode,
    aggregationKey,
    defaultAggregationFn,
    loading,
    { ...opts, traceKey: opts?.traceKey ?? `MuiTrendsChart-${cacheKey}` }
  );

  // Series with value formatters - derived directly, no separate state
  const series = useMemo(() => {
    if (chartData.isFetching || !chartData.data?.series) {
      return [];
    }
    const formatter = yAxisConfig?.valueFormatter;
    return formatter
      ? chartData.data.series.map((s: ChartDataSet) => ({
          ...s,
          valueFormatter: formatter,
        }))
      : chartData.data.series;
  }, [chartData.isFetching, chartData.data, yAxisConfig]);

  // X axis data
  const xAxisData = chartData.data?.xAxis ?? [];

  // Combined loading state
  const isLoading = loading || chartData.isLoading || chartData.isFetching;

  // Prevent render mismatch: if xAxis is empty but series has data, show loading
  const hasValidData = xAxisData.length > 0 || series.length === 0;

  return (
    <Box
      sx={{
        opacity: isLoading ? 0.5 : 1,
        transition: "opacity 0.2s",
      }}
    >
      {hasValidData ? (
        <LineChart
          id={cacheKey}
          xAxis={[
            {
              data: xAxisData,
              label: "Periodo",
              scaleType: "point",
              id: "primary",
            },
          ]}
          yAxis={yAxisConfig ? [yAxisConfig] : undefined}
          series={series as any}
          width={width}
          height={height}
          loading={isLoading}
          grid={grid}
          onAreaClick={normalizeAreaClick(xAxisData, onDataPointClick)}
          onAxisClick={normalizeAxisClick(xAxisData, onPeriodClick)}
        />
      ) : (
        <Box
          sx={{
            width,
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Placeholder durante il caricamento */}
        </Box>
      )}
    </Box>
  );
}